import React, { useState, useEffect, useCallback, useContext } from 'react';
import clsx from 'clsx';

import { makeStyles, TextField, CircularProgress, Grid, Paper } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import debounce from 'lodash/debounce';

import { fetchPickerRows } from 'Api';
import { AppContext } from 'AppContext';
import { BannerType } from '../Banner';

const defaultConfig = {
  loadingTime: 1000,
  rowsLimit: 50,
  pickerViewClass: "",
  autoSearch: false,
  newValueAtTheEnd: false
}

const useStyles = makeStyles((theme) => ({
  root: {
    fontSize: "13px",
    '&:hover': {
      backgroundColor: "#1fb6ad",
      color: "#ffffff"
    }
  },
  pickerRow: {
    borderTop: "1px solid rgba(0, 0, 0, 0)",
    textAlign: "center",
    overflowWrap: "break-word"
  },
  pickerNewRow: {
    color: "#1fb6ad",
    fontWeight: "bold",
    fontStyle: "italic",
    paddingRight: "0.5rem",
    paddingLeft: "0.5rem"
  },
  pickerViewHeader: {
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    fontSize: "13px",
    fontWeight: "bold",
    textAlign: "center",
    paddingLeft: "1rem",
    paddingRight: "2.0625rem"
  }
}));

export const PickerComponent = ({
  label,
  fieldName,
  value,
  disabled,
  onChange,
  onBlur,
  onCustomDataLoad,
  datasourceProperty,
  datasource,
  abortController,
  error,
  errorText,
  requestData,
  config,
  forceClearDatasource,
  Popup,
  onPopupSubmit,
  submitData,
  OptionsHeader,
  OptionView,
  setBannerState,
  setFormState,
  setValidationValue,
  setLoading,
  setAbortController,
  required,
  inputRef,
  setLookupData
}) => {
  const [dataLoading, setDataLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [popupState, setPopupState] = useState({ show: false, newValue: "" });
  const [pickerClassesState, setPickerClassesState] = useState({ popper: defaultConfig.pickerViewClass });
  /* AppContext is an external dependency, it should be extracted from PickerComponent in order to make this component resuable,
  when this component will be extracted to the hamilton web development */
  const appContext = useContext(AppContext);
  const classes = useStyles();

  useEffect(() => {
    if(value === null)
    {
      setRows([]);
    }
  }, [value]);

  useEffect(() => {
    if (forceClearDatasource && forceClearDatasource[fieldName]) {
      setRows([]);
    }
  }, [forceClearDatasource])

  const resetPickerClassState = useCallback(() => {
    if (rows?.length === 0) {
      setPickerClassesState(classState => ({
        ...classState,
        popper: defaultConfig.pickerViewClass
      }));
    }
  }, [rows, setPickerClassesState]);

  const loadRows = useCallback(async (requestPayload, filter) => {
    if (abortController == null)
    {
      abortController = new AbortController(); 
    }

    if(abortController.signal.aborted)
    {
      abortController = new AbortController();
      setAbortController(abortController)
    }

    if (setLookupData) {
      setLookupData(setRows, filter);
      if (config?.pickerViewClass) {
        setPickerClassesState(classState => ({
          ...classState,
          popper: config.pickerViewClass
        }));
      }
    }
    else {
      let request = { filter, ...requestPayload };
      let response = await fetchPickerRows(datasource, request, appContext.area, { signal: abortController.signal });

      if (response.canceled) {
        setDataLoading(false);
        setAbortController(new AbortController());
        return;
      }

      let data = response.data;
      if (datasourceProperty) {
        data = datasourceProperty.split('.').reduce((obj, key) => obj[key], data) ?? [];
      }

      if (response.success) {
        setRows(data);
        if (config?.pickerViewClass) {
          setPickerClassesState(classState => ({
            ...classState,
            popper: config.pickerViewClass
          }));
        }
      }

      if (onCustomDataLoad) {
        setRows(data);
        onCustomDataLoad(response, fieldName);
      } else if (!response.success) {
        setBannerState({
          show: true,
          type: BannerType.error,
          message: response.errorMessage
        });
      }
    }
    setDataLoading(false);
  }, [abortController, datasource, onCustomDataLoad, appContext.area, setPickerClassesState, setBannerState, setAbortController]);

  const getRowsQuery = useCallback(debounce(async filter => {
    await loadRows(requestData, filter);
  }, defaultConfig.loadingTime), [requestData]);

  const handleInputFocus = useCallback(async () => {
    if (forceClearDatasource?.hasOwnProperty(fieldName)) {
      delete forceClearDatasource[fieldName];
      setRows([]);
    }

    if (config?.autoSearch ?? defaultConfig.autoSearch) {
      resetPickerClassState();
      setDataLoading(true);
      await getRowsQuery("");
    }
  }, [resetPickerClassState, setDataLoading, getRowsQuery, forceClearDatasource]);

  const handleTextChange = useCallback(async event => {
    let text = event.target?.value;

    if (text) {
      if(abortController != null)
      {
        abortController.abort();
      }

      resetPickerClassState();
      await getRowsQuery(text);
      setDataLoading(true);
    }
  }, [abortController, resetPickerClassState, setDataLoading, getRowsQuery]);

  const handleChange = (_, selectedValue) => {
    if(abortController != null)
    {
      abortController.abort();
    }
    
    if(selectedValue === null) {
      setRows([]);
    }
    
    if (typeof selectedValue === 'string') {
      setPopupState({
        newValue: selectedValue,
        show: true
      });
    } else if (selectedValue && selectedValue.inputValue) {
      // Create a new value from the user input
      setPopupState({
        newValue: selectedValue.inputValue,
        show: true
      });
    } else {
      onChange(selectedValue);
    }
  }

  const filterOptions = (options, params) => {
    // Suggest the creation of a new value
    const addNewValue = () => {
      if (Popup) {
        let inputValue = params.inputValue || (value && value[fieldName]);
        if (inputValue) {
          let newValue = {
            inputValue,
            [fieldName]: `Add "${inputValue}" ${fieldName}`
          };

          if (config?.newValueAtTheEnd) {
            filteredRows.push(newValue);
          } else {
            filteredRows.unshift(newValue);
          }
        }
      }
    }

    let filteredRows = options;

    let limit = config?.rowsLimit ?? defaultConfig.rowsLimit;
    filteredRows.splice(limit);

    if (!dataLoading) {
      addNewValue();
    }

    return filteredRows;
  }

  const getOptionLabel = option => {
    // Value selected with enter, right from the input
    if (typeof option === 'string') {
      return option;
    }

    // Regular option
    return option[fieldName];
  }

  const renderInputElement = params => (
    <TextField
      {...params}
      required={required}
      label={label}
      onFocus={handleInputFocus}
      onChange={handleTextChange}
      error={error}
      helperText={errorText}
      inputRef={inputRef}
      InputProps={{
        ...params.InputProps,
        endAdornment: (
          <>
            {dataLoading ? <CircularProgress color="inherit" size={20} /> : null}
            {params.InputProps.endAdornment}
          </>
        )
      }}
    />
  );

  const PaperComponent = ({ children }) => {
    if (OptionsHeader && rows.length > 0) {
      return (
        <Paper>
          <OptionsHeader className={classes.pickerViewHeader} />
          {children}
        </Paper>
      );
    }

    return <Paper>{children}</Paper>;
  }

  const renderOption = option => {
    if (OptionView && !option.inputValue) {
      return <OptionView option={option} className={clsx(classes.root, classes.pickerRow)} />;
    }

    let className = option.inputValue ? classes.pickerNewRow : classes.pickerRow;

    return (
      <Grid container direction="row" spacing={1} className={clsx(classes.root, className)}>
        <Grid item xs={12}><div>{option[fieldName]}</div></Grid>
      </Grid>
    );
  }

  const renderPopup = () => {
    return popupState.show &&
      <Popup
        popupState={popupState}
        onPopupSubmit={onPopupSubmit}
        submitData={submitData}
        requestData={requestData}
        setPopupState={setPopupState}
        setFormState={setFormState}
        setValidationValue={setValidationValue}
        setBannerState={setBannerState}
        setLoading={setLoading}
      />;
  }

  return <>
    <Autocomplete
      id={fieldName}
      name={fieldName}
      value={value}
      classes={pickerClassesState}
      PaperComponent={PaperComponent}
      disabled={disabled}
      filterOptions={filterOptions}
      loading={dataLoading}
      onChange={handleChange}
      onBlur={onBlur}
      clearOnBlur
      clearOnEscape
      freeSolo
      autoHighlight
      handleHomeEndKeys
      options={rows}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      renderInput={renderInputElement}
    />
    {renderPopup()}
  </>;
}